import { IoIosArrowDown } from 'react-icons/io'
import { Button, Chapter, Container, Expandable, TimeIcons } from './styles'
import { useRef, useState } from 'react'
import { ReactComponent as CheckedSVG } from './assets/checked.svg'
import { ReactComponent as UncheckedSVG } from './assets/unchecked.svg'
import { useMount } from 'react-use'
import { IChapter, ILesson } from '../../hooks/use-get-menu-content/types'
import { useLessonParams } from '../../hooks/use-lesson-params'
import { MdOndemandVideo, MdOutlineMenuBook } from 'react-icons/md'
import { formatSeconds } from 'utils/formatSeconds'
import TooltipWrapper from 'pages/MyQuestionBooks/components/TooltipWrapper'

interface IProps {
  lesson: ILesson

  onChapterClick?(): void
}

const LessonDisclosure = (props: IProps) => {
  const { lesson } = props

  const { moduleId, courseId, lessonId, chapterId } = useLessonParams()

  const ref = useRef<HTMLDivElement>()
  const [height, setHeight] = useState(0)
  const [isOpen, setIsOpen] = useState(false)

  const shouldScroll = false

  const hasNoChapters = lesson.children.length === 0
  const isLessonActive = lessonId === lesson._id

  const getChapterHref = (chapter: IChapter) =>
    `/course/${courseId}/module/${moduleId}/lesson/${lesson._id}/chapter/${chapter?._id}`

  const renderEmptyInfo = () => {
    if (lesson.available.date) {
      return `Conteúdo disponível dia ${lesson.available.date}`
    }

    return 'Conteúdo em atualização'
  }

  const renderTimeIcons = (readingTime: number, videoTime: number, type: 'lesson' | 'chapter') => (
    <TimeIcons data-type={type}>
      {readingTime > 0 && (
        <TooltipWrapper text="Tempo de leitura" position="right">
          <div className="wrapper">
            <MdOutlineMenuBook size={16} />
            {formatSeconds(readingTime)}
          </div>
        </TooltipWrapper>
      )}

      {videoTime > 0 && (
        <TooltipWrapper text="Tempo de vídeo" position="right">
          <div className="wrapper">
            <MdOndemandVideo size={16} />
            {formatSeconds(videoTime)}
          </div>
        </TooltipWrapper>
      )}
    </TimeIcons>
  )

  const renderChapter = (chapter: IChapter) => {
    return (
      <Chapter
        key={chapter._id}
        to={getChapterHref(chapter)}
        data-is-active={chapterId === chapter?._id}
        onClick={chapter.hasNoObjects ? (event) => event.preventDefault() : props.onChapterClick}
        data-disabled={chapter.hasNoObjects}
        aria-disabled={chapter.hasNoObjects}
      >
        {chapter?.progress > 95 ? (
          <CheckedSVG className="checked" />
        ) : (
          <UncheckedSVG className="unchecked" />
        )}

        <div className="group">
          <p className="title">
            {lesson.position}.{chapter?.position} {chapter?.name}
          </p>

          {chapter.hasNoObjects && <p className="empty-info">Conteúdo em atualização</p>}

          {renderTimeIcons(chapter.readingTime, chapter.videoTime, 'chapter')}
        </div>
      </Chapter>
    )
  }

  useMount(() => {
    if (shouldScroll) {
      setTimeout(() => {
        ref.current.scrollIntoView({ behavior: 'smooth' })
      }, 1000)
    }
  })

  return (
    <Container ref={ref} data-is-open={isOpen}>
      <Button
        onClick={() => setIsOpen((s) => !s)}
        data-is-open={isOpen}
        data-is-active={isLessonActive}
        data-has-no-chapters={hasNoChapters}
        disabled={hasNoChapters}
      >
        {lesson.completed ? (
          <CheckedSVG className="checked" />
        ) : (
          <UncheckedSVG className="unchecked" />
        )}

        <div className="group">
          <p className="title">
            <span>{lesson.position}.</span> <span className="name">{lesson.name}</span>
          </p>

          {(lesson.hasNoObjects || hasNoChapters) && (
            <p className="empty-info">{renderEmptyInfo()}</p>
          )}

          {renderTimeIcons(lesson.readingTime, lesson.videoTime, 'lesson')}
        </div>
        {!hasNoChapters && <IoIosArrowDown className="arrow" />}
      </Button>

      <Expandable
        $height={height}
        data-is-open={isOpen}
        ref={(node) => setHeight(node?.scrollHeight)}
      >
        {lesson.children.map(renderChapter)}
      </Expandable>
    </Container>
  )
}

export default LessonDisclosure
